import { getHost } from '@utils/api-helper/host';
import fetch from '@api/fetchers';
import {
  AiSolutionError,
  AiSolutionResponse,
  AiSolutionSuccess,
  ChatHistory,
  FlowType,
  GetAiSolutionParams,
  IChatMessage
} from '@cont/DoubtsUnacademyAI/types';

const defaultHost = getHost();

const API_URL = `${defaultHost}v2/doubts/learner/ai/solution/`;

const getAiSolution = async ({
  goalUID,
  attachments = [],
  history = []
}: GetAiSolutionParams): Promise<AiSolutionResponse> => {
  try {
    const body = {
      goal_uid: goalUID,
      flow_type:
        history.length === 0
          ? FlowType.ASK_NEW_QUESTION
          : FlowType.CONTINUE_CHAT,
      attachments: attachments.map((att) => ({
        content_type: att.content_type,
        content: att.content
      })),
      history: history.map((entry) => ({
        rank: entry.rank,
        user_input: entry.user_input,
        ai_response: entry.ai_response,
        user_input_type: entry.user_input_type
      }))
    };

    const response = await fetch(API_URL, {
      requestType: 'POST',
      body,
      useCamelCase: false
    });

    if (response.error) {
      return {
        message: 'Something went wrong'
      } as AiSolutionError;
    }

    return response as AiSolutionSuccess;
  } catch (error) {
    return { message: 'Something went wrong' } as AiSolutionError;
  }
};

export default getAiSolution;

export function extractChatHistory(messages: IChatMessage[]): ChatHistory[] {
  return Object.values(
    messages.reduce((acc, msg) => {
      const key = msg.id; // Group by id
      acc[key] = acc[key] || { user: null, bot: null };

      if (msg.sender === 'user') acc[key].user = msg;
      if (msg.sender === 'bot') acc[key].bot = msg;

      return acc;
    }, {} as Record<number, { user: IChatMessage | null; bot: IChatMessage | null }>)
  )
    .filter(({ user, bot }) => user && bot) // Ensure complete pairs
    .sort((a, b) => b.user!.id - a.user!.id) // Sort by id (latest first)
    .slice(0, 3) // Take last 3 conversations
    .reverse()
    .map(({ user, bot }, index) => ({
      rank: index + 1, // Start from 1 instead of 0
      user_input: user!.text || user!.image || '',
      ai_response: bot!.text || '',
      user_input_type: user!.type
    }));
}
